// ═══════════════════════════════════════════════════════════════════════════════
// src/lib/tokens.js
// Design tokens for PracticeOS Lite. Imported as `import { C } from "../lib/tokens"`.
// Keeps the exact palette from PracticeOSLite_Full.jsx so the refactor is visual-identity-preserving.
// ═══════════════════════════════════════════════════════════════════════════════

export const C = {
  // Brand - teal family. Resolves to the practice's brand palette via CSS
  // vars set in Layout.jsx (staff) and PortalShell.jsx (patient portal).
  // Falls back to the original teal for any unwrapped surfaces or test envs.
  teal:        "var(--brand-primary, #0F6E56)",
  tealMid:     "var(--brand-text, #1D9E75)",
  tealLight:   "var(--brand-text, #5DCAA5)",
  tealBg:      "var(--brand-bg, #E1F5EE)",
  tealBorder:  "var(--brand-border, #9FE1CB)",
  tealDark:    "var(--brand-text, #085041)",
  // Brand-coloured TEXT: the practice colour lifted for legibility on the
  // current theme (brandPalette derives --brand-text per theme). Use for text
  // and icons; C.teal stays the fill colour.
  brandText:   "var(--brand-text, var(--brand-primary))",
  // Text on a brand-tinted panel: brand colour in light, white in dark.
  onBrandTint: "var(--pos-on-brand-tint)",

  // Legacy colour family. These now point at the semantic roles so every badge,
  // pill and chip in the app themes correctly. 1,700+ call sites read these
  // names, which is why the billing CPT blocks and disposition bars stayed pale
  // in dark mode - the codemod rewrote components but never this file.
  // Amber family
  amber:       "var(--pos-warn-fg)",
  amberBg:     "var(--pos-warn-bg)",
  amberBorder: "var(--pos-warn-bg-strong)",
  amberMid:    "var(--pos-warn-solid)",

  // Red
  red:         "var(--pos-danger-solid)",
  redBg:       "var(--pos-danger-bg)",
  redBorder:   "var(--pos-danger-bg-strong)",

  // Blue
  blue:        "var(--pos-info-fg)",
  blueBg:      "var(--pos-info-bg)",
  blueBorder:  "var(--pos-info-solid)",

  // Purple
  purple:      "var(--pos-ai-fg)",
  purpleBg:    "var(--pos-ai-bg)",
  purpleBorder:"var(--pos-ai-solid)",

  // Green
  green:       "var(--pos-success-fg)",
  greenBg:     "var(--pos-success-bg)",
  greenBorder: "var(--pos-success-bg-strong)",

  // Gold - VBP / incentive accents (AMH Quality Dashboard)
  gold:        "var(--pos-warn-fg)",
  goldBg:      "var(--pos-warn-bg)",

  // Deep text-on-tint shades for badge and pill foregrounds. Fixed semantic
  // colors, not brand-wrapped. Added June 2026 when the AMH CM tabs dropped
  // their self-contained token copies in favor of this file.
  amberText:   "var(--pos-warn-fg)",
  redText:     "var(--pos-danger-fg)",
  blueText:    "var(--pos-info-fg)",
  greenText:   "var(--pos-success-fg)",

  // Surfaces. These are CSS variables, not literals, so the theme class on
  // <html> can retarget every one of the ~284 call sites at once (UI item 7).
  bgPrimary:   "var(--pos-bg-primary)",
  bgSecondary: "var(--pos-bg-secondary)",
  bgTertiary:  "var(--pos-bg-tertiary)",

  // Text
  textPrimary:   "var(--pos-text-primary)",
  textSecondary: "var(--pos-text-secondary)",
  textTertiary:  "var(--pos-text-tertiary)",

  // Borders
  borderLight: "var(--pos-border-light)",
  borderMid:   "var(--pos-border-mid)",
  borderLightSolid: "var(--pos-border-light-solid)",
  onAccent: "var(--pos-on-accent)",
  quietOpacity: "var(--pos-quiet-opacity)",
  scrim: "var(--pos-scrim)",
  zoneCheckoutBg: "var(--pos-zone-checkout-bg)",
  zoneCheckoutBorder: "var(--pos-zone-checkout-border)",
  zoneCheckoutFg: "var(--pos-zone-checkout-fg)",
  zoneFloorBg: "var(--pos-zone-floor-bg)",
  zoneFloorBorder: "var(--pos-zone-floor-border)",
  zoneFloorFg: "var(--pos-zone-floor-fg)",
  zoneLobbyFg: "var(--pos-zone-lobby-fg)",
  hairline: "var(--pos-hairline)",
  hairlineStrong: "var(--pos-hairline-strong)",

  // Semantic roles (dark mode pass 1). Component code names the ROLE, never a
  // colour, so light and dark are one variable swap. Values live in styles.css.
  dangerBg: "var(--pos-danger-bg)",
  dangerBgStrong: "var(--pos-danger-bg-strong)",
  dangerFg: "var(--pos-danger-fg)",
  dangerSolid: "var(--pos-danger-solid)",
  dangerSoft: "var(--pos-danger-soft)",
  warnBg: "var(--pos-warn-bg)",
  warnBgStrong: "var(--pos-warn-bg-strong)",
  warnFg: "var(--pos-warn-fg)",
  warnSolid: "var(--pos-warn-solid)",
  successBg: "var(--pos-success-bg)",
  successBgStrong: "var(--pos-success-bg-strong)",
  successFg: "var(--pos-success-fg)",
  successSolid: "var(--pos-success-solid)",
  infoBg: "var(--pos-info-bg)",
  // Border tone for a tinted info surface - the info sibling of
  // warnBgStrong / successBgStrong, which existed while this did not, which is
  // why info borders were being written as raw hex.
  infoBgStrong: "var(--pos-info-bg-strong)",
  infoFg: "var(--pos-info-fg)",
  infoSolid: "var(--pos-info-solid)",
  aiBg: "var(--pos-ai-bg)",
  aiFg: "var(--pos-ai-fg)",
  // Border tone for a tinted AI surface, the ai sibling of infoBgStrong.
  aiBgStrong: "var(--pos-ai-bg-strong)",
  // Fixed identity tones, independent of the practice's brand palette.
  toneTealBg: "var(--pos-tone-teal-bg)",
  toneTealFg: "var(--pos-tone-teal-fg)",
  tonePinkBg: "var(--pos-tone-pink-bg)",
  tonePinkFg: "var(--pos-tone-pink-fg)",
  aiSolid: "var(--pos-ai-solid)",
  aiSurface: "var(--pos-ai-surface)",
  aiLine: "var(--pos-ai-line)",
  aiMuted: "var(--pos-ai-muted)",

  // Nav
  navBg:       "#0a0a0a",
  // Text and hairlines ON the nav / stage surface. Fixed like navBg: the
  // surface is near-black in both themes, so these do not follow the theme
  // ramps (which would go dark-on-dark in light mode). 4.5:1+ on #0a0a0a.
  navText:     "#E6E6E6",
  navMuted:    "#A3A3A3",
  navLine:     "#262626",
  // Attention fill for a nav glyph. Fixed, like navBg: the nav is #0a0a0a in
  // both themes, so this amber sits on a constant surface and must not follow
  // the practice brand or the warn ramp.
  navAttention: "#F0B429",
  // Role identity dots in the sidebar user strip. Fixed for the same reason as
  // navBg: the ROLE_STYLES colours are theme tokens tuned for the page, and on
  // the always-dark nav several went dark-on-dark (Care Manager measured
  // 1.26:1 in dark). The role NAME now uses navMuted; these only tint the dot
  // beside it. Each is 7:1 or better on #0a0a0a.
  navRolePurple: "#A78BFA",
  navRoleBlue:   "#60A5FA",
  navRoleGreen:  "#4ADE80",
  navRoleTeal:   "#5DCAA5",
  navRoleAmber:  "#FBBF24",

  // Elevation scale (July 2026 UI polish pass). Soft, layered shadows tuned to
  // sit lightly on the warm neutral surfaces, tinted with the app's dark-green
  // neutral rather than flat black so they read warm. Adopt via
  // C.shadowSm / shadowMd / shadowLg so elevation stays consistent and is
  // tunable in one place.
  // Legacy shadow names, kept because 9 call sites still read them. They now
  // point at the themed elevation levels rather than carrying their own
  // light-only values, so there is one elevation system, not two.
  shadowXs: "var(--pos-shadow-raised)",
  shadowSm: "var(--pos-shadow-raised)",
  shadowMd: "var(--pos-shadow-hover)",
  shadowLg: "var(--pos-shadow-floating)",
  // Elevation scale (UI Excellence item 2). Five named levels; components pick a level, never a shadow.
  //   flat     - border only (dense lists, table rows)
  //   raised   - in-flow cards (brand-tinted contact shadow)
  //   floating - dropdowns, popovers, toasts
  //   overlay  - modals, drawers
  //   glass    - chrome: sticky headers, strips, palettes. Use the .pos-glass-* CSS classes, not a shadow.
  elevation: {
    flat:     "none",
    raised:   "var(--pos-shadow-raised)",
    hover:    "var(--pos-shadow-hover)",
    floating: "var(--pos-shadow-floating)",
    overlay:  "var(--pos-shadow-overlay)",
  },

  // Corner radius scale.
  radiusSm:  8,
  radiusMd:  12,
  radiusLg:  16,
};

// Role visual-identity map -----------------------------------------------------
// Mirrors the ROLES const from PracticeOSLite_Full.jsx but keyed by the new
// Title Case enum values that match public.user_role in Postgres.
// color / bg / border are THEME tokens for badges on page surfaces. navDot is
// the fixed tint for the sidebar user strip, which is #0a0a0a in both themes;
// never put color / border on the nav (check-dark-mode-readiness enforces it).
export const ROLE_STYLES = {
  "Owner":             { label: "Owner",             color: C.purple,   bg: C.purpleBg, border: C.purpleBorder, navDot: C.navRolePurple },
  "Manager":           { label: "Practice Manager",  color: C.blue,     bg: C.blueBg,   border: C.blueBorder,   navDot: C.navRoleBlue   },
  "Provider":          { label: "Provider",          color: C.green,    bg: C.greenBg,  border: C.greenBorder,  navDot: C.navRoleGreen  },
  "Medical Assistant": { label: "Medical Assistant", color: C.brandText, bg: C.tealBg,   border: C.tealBorder,   navDot: C.navRoleTeal   },
  "Front Desk":        { label: "Front Desk",        color: C.blue,     bg: C.blueBg,   border: C.blueBorder,   navDot: C.navRoleBlue   },
  "Billing":           { label: "Billing",           color: C.amber,    bg: C.amberBg,  border: C.amberBorder,  navDot: C.navRoleAmber  },
  "Patient":           { label: "Patient",           color: C.textSecondary, bg: C.bgSecondary, border: C.borderLight, navDot: C.navMuted },

  // Care Management roles (Stage 2c - Command tier)
  "Care Manager":              { label: "Care Manager",            color: C.brandText, bg: C.tealBg,    border: C.tealBorder,    navDot: C.navRoleTeal   },
  "Supervising Care Manager":  { label: "Supervising CM",          color: C.tealDark, bg: C.tealBg,    border: C.tealBorder,    navDot: C.navRoleTeal   },
  "Care Manager Supervisor":   { label: "CM Supervisor",           color: C.purple,   bg: C.purpleBg,  border: C.purpleBorder,  navDot: C.navRolePurple },
  "CHW":                       { label: "Community Health Worker", color: C.amber,    bg: C.amberBg,   border: C.amberBorder,   navDot: C.navRoleAmber  },

  // Clinical-support roles (Phase 7 enum additions, May 2026)
  "RN":                        { label: "Registered Nurse",        color: C.green,    bg: C.greenBg,   border: C.greenBorder,   navDot: C.navRoleGreen  },
  "LPN":                       { label: "Licensed Practical Nurse",color: C.green,    bg: C.greenBg,   border: C.greenBorder,   navDot: C.navRoleGreen  },
  "Pharmacist":                { label: "Pharmacist",              color: C.green,    bg: C.greenBg,   border: C.greenBorder,   navDot: C.navRoleGreen  },
  "Health Educator":           { label: "Health Educator",         color: C.amber,    bg: C.amberBg,   border: C.amberBorder,   navDot: C.navRoleAmber  },
};

// Which nav tabs each role can see. Matches the schema's role-based RLS.
// 'kiosk' and 'compliance' and 'insights' are the new modules.
// Roles whose Inbox is Documents only (Oct 2026): Billing files EOBs, cards
// and payer letters but does not work patient messages or escalations.
export const INBOX_DOCS_ONLY_ROLES = ["Billing"];

export const NAV_BY_ROLE = {
  "Owner":             ["dashboard","schedule","care_schedule","patients","queue","tasks","clinical","inbox","staff","eligibility","insurance_updates","waitlist","insights","compliance","consents","care_management","billing","reports","settings","recall","campaigns","referrals"],
  "Manager":           ["dashboard","schedule","care_schedule","patients","queue","tasks","inbox","staff","eligibility","insurance_updates","waitlist","insights","compliance","consents","care_management","billing","reports","settings","recall","campaigns","referrals"],
  "Provider":          ["dashboard","schedule","patients","clinical","inbox","insights","referrals"],
  "Medical Assistant": ["dashboard","schedule","patients","queue","tasks","inbox","referrals"],
  "Front Desk":        ["dashboard","schedule","patients","queue","tasks","inbox","waitlist","eligibility","insurance_updates","recall","referrals","reports"],
  "Billing":           ["dashboard","billing","inbox","eligibility","insurance_updates","reports","compliance","recall","referrals"],
  "Patient":           ["portal"],

  // Care Management roles (Stage 2c - Command tier)
  "Care Manager":              ["dashboard","care_schedule","patients","care_management","inbox","tasks"],
  "Supervising Care Manager":  ["dashboard","care_schedule","patients","care_management","inbox","tasks","reports","campaigns"],
  "Care Manager Supervisor":   ["dashboard","care_schedule","patients","care_management","inbox","tasks","reports","insights","campaigns"],
  "CHW":                       ["chw_engagements","dashboard","my_people","care_schedule","patients","tasks"],
  // Clinical-support roles (Phase 7 enum additions, May 2026). Defaults
  // chosen by analogy: RN/LPN like Medical Assistant, Pharmacist limited
  // (no clinical, no schedule). Health Educator has no Care Management
  // console (Oct 2026): the console gate and the CM table RLS are limited to
  // Owner, Manager and the three care manager roles, so the nav entry was a
  // dead end. Education work happens from the patient chart and tasks.
  "RN":                        ["dashboard","schedule","patients","queue","tasks","inbox","referrals"],
  "LPN":                       ["dashboard","schedule","patients","queue","tasks","inbox","referrals"],
  "Pharmacist":                ["dashboard","patients","tasks"],
  "Health Educator":           ["dashboard","patients","tasks"],
};

export const NAV_META = {
  dashboard:   { icon: "⊞",  label: "Dashboard"   },
  schedule:    { icon: "📅", label: "Schedule"    },
  patients:    { icon: "👥", label: "Patients"    },
  queue:       { icon: "⬡",  label: "Queue"       },
  tasks:       { icon: "✓",  label: "Tasks"       },
  clinical:    { icon: "🩺", label: "Clinical"    },
  inbox:       { icon: "✉",  label: "Inbox"       },
  staff:       { icon: "👤", label: "Staff"       },
  eligibility:       { icon: "◈",  label: "Eligibility"       },
  insurance_updates: { icon: "🪪", label: "Insurance Updates" },
  recall:            { icon: "📣", label: "Recall" },
  campaigns:         { icon: "📈", label: "Campaigns" },
  waitlist:          { icon: "⌛", label: "Waitlist"           },
  referrals:         { icon: ">",  label: "Referrals & Auths"  },
  insights:    { icon: "📊", label: "Insights" },
  compliance:  { icon: "🛡", label: "Compliance"  },
  consents:    { icon: "✍",  label: "Consents"    },
  care_management: { icon: "🫶", label: "Care Management" },
  care_schedule:   { icon: "📋", label: "Care Schedule"   },
  reports:     { icon: "📈", label: "Reports"     },
  billing:     { icon: "🧾", label: "Billing"     },
  settings:    { icon: "⚙",  label: "Settings"    },
  portal:           { icon: "☰",  label: "My Health"      },
  chw_engagements:  { icon: "🤝", label: "My Engagements" },
  my_people:        { icon: "📇", label: "My People"      },
  // Personal settings (/my-settings). Not in NAV_BY_ROLE: Layout.jsx adds it
  // for any staff user with a provider record, whatever the role.
  my_settings:      { icon: "\u2699", label: "My Settings"    },
};

// Sidebar section organization. Layout.jsx walks this in order, intersects
// each section's items with the role's NAV_BY_ROLE array, and skips empty
// sections. Pro · AI and Super Admin are NOT in this list - they're rendered
// separately because their items come from prefs (Pro AI features) and an
// account flag (isSuperAdmin), not from NAV_BY_ROLE.
//
// chw_engagements lives in "Today" alongside Schedule / Care Schedule because
// it functions as the CHW's daily personal queue.
export const NAV_SECTIONS = [
  { key: "today",     label: "Today",           items: ["dashboard","chw_engagements","schedule","care_schedule","queue","inbox","tasks"] },
  { key: "patients",  label: "Patients",        items: ["my_people","patients","clinical","referrals","waitlist"] },
  { key: "revenue",   label: "Revenue",         items: ["eligibility","insurance_updates","billing","recall","campaigns"] },
  { key: "reporting", label: "Reporting",       items: ["reports","insights"] },
  { key: "care_mgmt", label: "Care management", items: ["care_management"] },
  { key: "practice",  label: "Practice",        items: ["staff","compliance","consents","settings"] },
  { key: "personal",  label: "Personal",        items: ["my_settings"] },
];

// 15-min slot constants (preserved from existing UI)
export const SLOT_H = 22;
export const TIME_COL_W = 64;

export const DAYS_OF_WEEK = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

// ---------------------------------------------------------------------------
// Design scale (Sep 2026). Inline styles may use ONLY these values for
// fontSize and numeric borderRadius; scripts/check-design-scale.mjs fails the
// build otherwise. Before the sweep the app used 34 font sizes (including
// half-pixels like 11.5 and 12.5) and 20 radii, which made adjacent screens
// read as slightly different products.
//
//   FS: micro 8-9 (badge counts, thumbnails) | caption 10-11 | small 12 |
//       body 13 | ui 14 | title 16-18 | heading 20-24 | display 28+
//   R:  0-2 hairline bars | 4 chips | 6 controls | 8 cards and rows |
//       10-12 panels | 16-20 sheets | 999 pills (use "50%" for circles)
// ---------------------------------------------------------------------------
export const FS = { micro: 9, caption: 11, small: 12, body: 13, ui: 14, title: 16, heading: 22, display: 28 };
export const R  = { chip: 4, control: 6, card: 8, panel: 12, sheet: 16, pill: 999 };
export const FONT_SIZE_SCALE = [8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24, 28, 32, 36, 40, 44, 48];
export const RADIUS_SCALE    = [0, 1, 2, 4, 6, 8, 10, 12, 16, 20, 999];
